/** biome-ignore-all lint/suspicious/noExplicitAny: legacy auth/redux/tenant shapes */
/** biome-ignore-all lint/correctness/useExhaustiveDependencies: legacy dep lists preserved */
import * as sessionManager from '@bb/session-manager';
import { useAuth } from '@bb/session-manager';
import { Badge, Box, Button, Flex, IconButton, Menu, Text, useMediaQuery } from '@rocket/ui';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LuBellRing,
  LuChevronDown,
  LuCircleHelp,
  LuCircleUserRound,
  LuListChecks,
  LuMenu,
  LuMonitor,
  LuMoon,
  LuPlus,
  LuSun,
} from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';

import Logo from '../../assets/images/logo-icon-white.png';
import { useCanChangePassword } from '../../auth/useCanChangePassword';
import { LOG_LOGOUT_ENDPOINT, USERGUIDE_ENDPOINT } from '../../endpoints.config';
import { ACCOUNT_ADMIN_PERMISSION, BLACKBOILER_ADMIN_PERMISSION } from '../../groups.config';
import { openJobs, startCheckingJobs } from '../../redux/actions/asyncJobActions';
import { setErrMsg, setSuccessMsg } from '../../redux/actions/snackbarActions';
import { ChangeTenant, GetCurrentTenant, GetTenants } from '../../requests/tenantManagement';
import { ACCOUNT_ROUTE, TENANTS_NEW_ROUTE } from '../../routes.config';
import Colors from '../../styles/Colors';
import { useColorMode } from '../../styles/colorMode';
import AsyncJobsDialog from '../AsyncJobs/AsyncJobsDialog';
import { ChangePasswordDialog } from '../ChangePasswordDialog';
import { LabeledSelect } from '../LabeledSelect';

type AnyData = any;

const TRUNCATED_LINKS = [
  'Home',
  'Contracts',
  'Users',
  'Clause Library',
  'Playbook Builder',
  'PoA',
  'Reporting',
];

const MINI_LINKS = ['Home', 'Contracts', 'Users', 'PoA'];

interface NavItem {
  label: string;
  path?: string;
  role?: boolean;
  external?: boolean;
  children?: Array<NavItem>;
}

interface AppBarFullProps {
  pathname: string;
  navList: Array<NavItem>;
  companyName?: string;
  userName: string;
}

const NAV_TEXT_STYLES = {
  color: 'white',
  fontSize: '15px', // was 1.5rem @ 62.5% root
  letterSpacing: '0',
  whiteSpace: 'nowrap',
} as const;

const AppBarFull = ({ navList, companyName = '', userName, pathname }: AppBarFullProps) => {
  const { config, setConfig, user, bbAxios, signOut } = useAuth() as AnyData;

  const { jobs } = useSelector((state: AnyData) => state.AsyncJob);

  const navigate = useNavigate();
  const { colorScheme, cycleColorScheme } = useColorMode();

  const dispatch = useDispatch();
  const setSuccess = (value: string) => dispatch(setSuccessMsg(value));
  const setErr = (value: string) => dispatch(setErrMsg(value));

  const [tenant, setTenant] = useState<AnyData>(null);
  const [allTenants, setAllTenants] = useState<Array<AnyData>>([]);
  const [isChangingTenant, setIsChangingTenant] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const canChangePassword = useCanChangePassword();

  const jobsAnchorRef = useRef<HTMLButtonElement>(null);

  const [isTruncated] = useMediaQuery(['(max-width: 1444px)']);
  const [isMini] = useMediaQuery(['(max-width: 1150px)']);
  const [isHidden] = useMediaQuery(['(max-width: 800px)']);

  useEffect(() => {
    const asyncEffect = async () => {
      await checkJobs();
      // Only load tenant data if multi-tenant management is enabled and user has permission
      if (config?.useTenantManagement && user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION)) {
        await loadTenantData();
      }
    };
    asyncEffect().catch((err) => console.error(err));
  }, [config, user]);

  const showTenantSelect = useMemo(
    () => !!tenant && !!Object.keys(tenant).length && allTenants.length > 0,
    [tenant, allTenants]
  );

  const loadTenantData = async () => {
    try {
      const [currentTenant, tenants] = await Promise.all([GetCurrentTenant(), GetTenants()]);
      setTenant(currentTenant);
      setAllTenants(tenants || []);
    } catch (error) {
      console.error('Failed to load tenant data:', error);
    }
  };

  const checkJobs = async () => {
    dispatch(startCheckingJobs(dispatch, { disableRefresh: true }) as AnyData);
  };

  const handleAdminItemClick = useCallback(
    async (item: NavItem) => {
      if (item.external) {
        try {
          const res = await fetch(item.path as string, { method: 'HEAD' });
          if (res.ok || res.status === 200) {
            window.open(item.path, '_blank', 'noopener,noreferrer');
          } else {
            console.warn(
              `[Admin] Text Indexer not available in this deployment (HTTP ${res.status})`
            );
            dispatch(setErrMsg('Text Indexer is not available in this deployment'));
          }
        } catch (err) {
          console.warn('[Admin] Text Indexer HEAD check threw:', err);
          dispatch(setErrMsg('Text Indexer is not available in this deployment'));
        }
      } else {
        // rocket Menu closes on select; defer navigation past the close animation
        setTimeout(() => navigate({ to: item.path }), 300);
      }
    },
    [navigate, dispatch]
  );

  const handleLogout = () => {
    // Log the logout event (fire-and-forget, don't wait for response)
    // This prevents the axios interceptor from interfering with the logout flow
    bbAxios
      .get(LOG_LOGOUT_ENDPOINT)
      .catch((err: AnyData) => console.error('Failed to log the user logout with error:', err));

    // Immediately sign out from the auth provider
    // This will redirect the user to the Cognito logout endpoint
    signOut();
  };

  const isActivePath = (path: string) => {
    const [, , thirdInPathname] = pathname.split('/');
    const [, , thirdInPath] = path.split('/');
    return thirdInPathname === thirdInPath;
  };

  const handleHelpIconClick = (event: React.MouseEvent) => {
    event.preventDefault();
    bbAxios.get(USERGUIDE_ENDPOINT, { responseType: 'blob' }).then((resp: AnyData) => {
      // Build a URL from the file
      const fileURL = URL.createObjectURL(resp.data);
      // Open the URL on new Window
      window.open(fileURL);
    });
  };

  const handleSetTenant = async (newTenantId: string) => {
    if (newTenantId === 'create') {
      navigate({ to: TENANTS_NEW_ROUTE });
    } else {
      try {
        setIsChangingTenant(true);
        // Get the socket ID from the live /global/event namespace so the backend
        // can exclude THIS tab from the "tenant changed" notification.
        // (getNamespace exists at runtime but session-manager's types omit it.)
        const globalSocket = (sessionManager as AnyData).getNamespace('/global/event');
        await ChangeTenant(newTenantId, globalSocket?.id ?? null);

        // Fetch fresh config with new tenant permissions using V2 API
        const response = await bbAxios.get('/api/v2/config');
        if (response.data) {
          setConfig(response.data);
          setSuccess('Tenant changed successfully');
          navigate({ to: '/bbweb/home' });
        } else {
          // Fallback to reload if fetch fails
          setSuccess('Tenant changed');
          window.location.reload();
        }
      } catch (err) {
        const e = err as AnyData;
        console.error('Failed to change tenant:', e);
        const errorMsg = e.response?.data?.message || e.message || 'Failed to change tenant';
        setErr(errorMsg);

        // If it's a 401, the user might need to reload to get proper tenant context
        if (e.response?.status === 401) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } finally {
        setIsChangingTenant(false);
      }
    }
  };

  const renderLinks = useMemo(
    () =>
      navList
        .filter(({ label }) => {
          if (isTruncated) {
            if (isHidden) {
              return false;
            }
            if (isMini) {
              return MINI_LINKS.includes(label);
            }
            return TRUNCATED_LINKS.includes(label);
          }
          return true;
        })
        .map(({ label, path, role, children }) => {
          if (!role) {
            return null;
          }
          if (children) {
            return (
              <Menu
                key="admin-dropdown"
                items={children.map((item) => ({
                  value: item.label,
                  label: item.label,
                  onClick: () => handleAdminItemClick(item),
                }))}
                trigger={
                  <Button
                    variant="plain"
                    {...NAV_TEXT_STYLES}
                    fontWeight={children.some((c) => isActivePath(c.path as string)) ? 700 : 400}
                    padding="0"
                    minWidth="0"
                    minHeight="0"
                  >
                    {label}
                    <LuChevronDown />
                  </Button>
                }
              />
            );
          }
          return (
            <RouterLink key={path} to={path} style={{ textDecoration: 'none' }}>
              <Text {...NAV_TEXT_STYLES} fontWeight={isActivePath(path as string) ? 700 : 400}>
                {label}
              </Text>
            </RouterLink>
          );
        }),
    [navList, isTruncated, isMini, isHidden, handleAdminItemClick]
  );

  const hamburgerItems = useMemo(
    () =>
      navList
        .filter(({ label, role }) => {
          // Only include items the user has permission for
          if (!role) {
            return false;
          }
          if (isTruncated) {
            if (isHidden) {
              return true;
            }
            if (isMini) {
              return !MINI_LINKS.includes(label);
            }
            return !TRUNCATED_LINKS.includes(label);
          }
          return false;
        })
        .flatMap(({ label, path, children }) => {
          // Items with children (e.g. Admin dropdown) — expand into individual menu items
          if (children) {
            return children.map((child) => ({
              value: child.label,
              label: child.label,
              onClick: () => handleAdminItemClick(child),
            }));
          }
          return [
            {
              value: label,
              label,
              onClick: () => navigate({ to: path }),
            },
          ];
        }),
    [navList, handleAdminItemClick, navigate, isTruncated, isMini, isHidden]
  );

  // Only show hamburger menu if there are actually items to display
  const hasMenuItems = hamburgerItems.length > 0;

  const pendingJobs = jobs?.filter((j: AnyData) =>
    ['pending', 'processing'].includes(j.status)
  ).length;

  const profileItems = [
    ...(user?.permissions?.has(ACCOUNT_ADMIN_PERMISSION)
      ? [{ value: 'account', label: 'Account', onClick: () => navigate({ to: ACCOUNT_ROUTE }) }]
      : []),
    ...(canChangePassword
      ? [
          {
            value: 'change-password',
            label: 'Change Password',
            onClick: () => setIsChangePasswordOpen(true),
          },
        ]
      : []),
    { value: 'sign-out', label: 'Sign Out', onClick: handleLogout },
  ];

  return (
    <>
      <Flex width="100%" justifyContent="space-between" alignItems="center" gap="20px">
        <Flex alignItems="center" gap="20px">
          <RouterLink to="/">
            <img
              src={Logo}
              alt="blackboiler logo"
              style={{
                marginBottom: '3px',
                display: 'block',
                width: '30px',
                height: '30px',
                objectFit: 'contain',
              }}
            />
          </RouterLink>

          {renderLinks}

          {isTruncated && hasMenuItems && (
            <Menu
              items={hamburgerItems}
              trigger={
                <IconButton
                  aria-label="Navigation menu"
                  variant="plain"
                  color="white"
                  marginLeft="-12px"
                >
                  <LuMenu />
                </IconButton>
              }
            />
          )}
        </Flex>

        <Flex alignItems="center" gap="8px">
          {showTenantSelect && allTenants.length > 1 ? (
            <Box display="inline-flex" alignItems="center" gap="8px">
              <LabeledSelect
                title=""
                value={tenant._id}
                setValue={(evt: AnyData) => handleSetTenant(evt)}
                items={allTenants}
                keyField="_id"
                valueField="displayName"
                disabled={isChangingTenant}
                className="bb-appbar-control"
                styleProps={{
                  fontWeight: 'bold',
                  color: Colors.trueWhite,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.24)',
                  borderRadius: '6px',
                  opacity: isChangingTenant ? 0.6 : 1,
                  cursor: isChangingTenant ? 'not-allowed' : 'pointer',
                }}
              />
              {!!config?.useTenantManagement &&
                user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION) && (
                  <button
                    type="button"
                    onClick={() => handleSetTenant('create')}
                    className="bb-appbar-control"
                    style={{
                      fontWeight: 'bold',
                      color: Colors.trueWhite,
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.24)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    Create Tenant <LuPlus />
                  </button>
                )}
            </Box>
          ) : (
            <Text display="inline" color="white" fontSize="15px" marginRight="12px">
              {allTenants && !!allTenants.length && allTenants.length === 1
                ? allTenants[0].displayName
                : companyName}
            </Text>
          )}

          <IconButton
            aria-label={`Theme: ${colorScheme} (click to change)`}
            title={`Theme: ${colorScheme}`}
            variant="plain"
            color="white"
            size="lg"
            onClick={cycleColorScheme}
          >
            {colorScheme === 'system' ? (
              <LuMonitor />
            ) : colorScheme === 'dark' ? (
              <LuMoon />
            ) : (
              <LuSun />
            )}
          </IconButton>

          <IconButton
            aria-label="User guide"
            variant="plain"
            color="white"
            size="lg"
            onClick={handleHelpIconClick}
          >
            <LuCircleHelp />
          </IconButton>

          <IconButton aria-label="Notifications" variant="plain" color="white" size="lg">
            <LuBellRing />
          </IconButton>

          <IconButton
            aria-label="Jobs"
            title="Jobs"
            variant="plain"
            color="white"
            size="lg"
            position="relative"
            onClick={() => dispatch(openJobs() as AnyData)}
            ref={jobsAnchorRef}
          >
            <LuListChecks />
            {!!pendingJobs && (
              <Badge
                colorPalette="blue"
                variant="solid"
                position="absolute"
                top="0"
                right="0"
                borderRadius="full"
              >
                {pendingJobs}
              </Badge>
            )}
          </IconButton>

          <Menu
            items={profileItems}
            trigger={
              <Button variant="plain" color="white" padding="0" textTransform="capitalize">
                <LuCircleUserRound style={{ margin: '0 4px 0 16px' }} />
                {userName}
                <LuChevronDown />
              </Button>
            }
          />
        </Flex>
      </Flex>

      <AsyncJobsDialog anchorEl={jobsAnchorRef.current} />
      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </>
  );
};

export default AppBarFull;
